/* global process */
//Utility functions for API call logic.
import { mapFieldsToCamelCase } from "./cardUtils";
import { mapActionsToCamelCase } from "./actionUtils";
import { parseNotificationTimestamp } from "./chartUtils";
import * as cacheUtils from "./cacheUtils";

const CARDS_SERVER = process.env.REACT_APP_CARDS_SERVER;
const ACTIONS_SERVER = process.env.REACT_APP_ACTIONS_SERVER;
const NOTIFICATIONS_SERVER = process.env.REACT_APP_NOTIFICATIONS_SERVER;

// Cache for API responses. Each entry is { response: Response, timestamp: number }.
const apiResponseCache = new Map();

// How long a cached response stays usable. Kept slightly above the throttle
// interval so throttled retries can reuse a fresh response, but stale entries
// (e.g. a slow response that landed long after the app gave up) are discarded.
const API_CACHE_TTL = 10000; // 10 seconds

export function fetchWithTimeout(url, options = {}, timeout = 15000) {
    const method = (options.method || 'GET').toUpperCase();
    const skipCache = options.skipCache === true || method !== 'GET';
    const fetchOptions = { ...options };
    delete fetchOptions.skipCache;
    // Generate a cache key based on URL and method
    const cacheKey = `${url}-${method}`;
    
    // Mutations and explicit React Query reads must not replay a previous body.
    if (!skipCache) {
        const allowCall = shouldAllowApiCall(url);
        if (!allowCall && apiResponseCache.has(cacheKey)) {
            const cached = apiResponseCache.get(cacheKey);
            if (Date.now() - cached.timestamp < API_CACHE_TTL) {
                return Promise.resolve(cached.response.clone());
            }
            apiResponseCache.delete(cacheKey);
        }
    }

    // Use a real AbortController so a timed-out request is actually cancelled.
    // The previous Promise.race() approach rejected the promise but left the
    // underlying fetch running, leaving zombie connections pending in the
    // network tab and exhausting the browser's per-host connection pool.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Respect a caller-supplied AbortSignal by forwarding its abort to ours.
    if (fetchOptions.signal) {
        if (fetchOptions.signal.aborted) {
            controller.abort();
        } else {
            fetchOptions.signal.addEventListener('abort', () => controller.abort(), { once: true });
        }
    }

    return fetch(url, { ...fetchOptions, signal: controller.signal })
        .then(response => {
            // Only cache successful GETs. POST/PATCH/DELETE must not be replayed.
            if (!skipCache && method === 'GET' && response.ok) {
                apiResponseCache.set(cacheKey, { response: response.clone(), timestamp: Date.now() });
            }
            return response;
        })
        .catch(error => {
            // Normalize an abort/timeout into the error message callers expect
            if (error && error.name === 'AbortError') {
                throw new Error('Request timed out');
            }
            throw error;
        })
        .finally(() => clearTimeout(timeoutId));
}

// Helper function to extract username from userVal cache
const getUserKey = () => {
    const userValItem = cacheUtils.get("userVal");
    if (!userValItem) {
        return null;
    }
    // Extract the username from the userVal structure
    return userValItem.data || userValItem;
};

export async function fetchCardDataDB() {
    const userKey = getUserKey();
    if (!userKey) {
        console.warn("fetchCardDataDB: No user key found");
        return [];
    }

    let cardData = [];
    try {
        const response = await fetchWithTimeout(`${CARDS_SERVER}${userKey}`, {method: 'GET'});

        if (!response.ok) {
            throw new Error(`fetchCardDataDB failed with status: ${response.status}`);
        }

        const data = await response.json();

        cardData = mapFieldsToCamelCase(data);
        
        if (cardData.length === 0) {
            console.warn(`No card data found for user: ${userKey}`);
        }
    }
    catch (error) {
        console.error(`Error fetching card data for user ${userKey}:`, error);
    }

    return cardData;
}

export async function postCardDataDB(cards) {
    const userKey = getUserKey();
    if (!userKey) {
        console.warn("postCardDataDB: No user key found");
        return false;
    }
    
    // Use provided cards or get from localStorage as fallback
    const updatedCards = cards || cacheUtils.get(userKey, []);
    
    try {
        const response = await fetchWithTimeout(`${CARDS_SERVER}update/${userKey}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedCards)
        });

        if (response.ok) {
            // Clear the cached cards after successful posting
            cacheUtils.remove(`${userKey}`);
            return true;
        }
        else {
            console.error("Failed to update cards.");
            return false;
        }
    }
    catch (error) {
        console.error("Error updating cards: ", error);
        return false;
    }
}

/*
@app.route('/api/actions/<username>', methods=['GET'])
@app.route('/api/actions/update/<user_key>', methods=['POST'])
*/
export async function fetchActionDataDB() {
    const userKey = getUserKey();
    if (!userKey) {
        console.warn("fetchActionDataDB: No username provided");
        return [];
    }

    let actionData = [];
    try {
        const response = await fetchWithTimeout(`${ACTIONS_SERVER}${userKey}`, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error(`fetchActionDataDB: failed with status: ${response.status}`);
        }

        const data = await response.json();

        actionData = mapActionsToCamelCase(data);

        if (actionData.length === 0) {
            console.warn(`No action data found for user: ${userKey}`);
        }
    } catch (error) {
        console.error(`Error fetching action data for user ${userKey}:`, error);
    }

    return actionData;
}

export async function postActionDataDB(actions) {
    const userKey = getUserKey();
    if (!userKey) {
        console.warn("postActionDataDB: No user key found");
        return false;
    }
    
    // Use provided actions or get from localStorage as fallback
    const updatedActions = actions || cacheUtils.get(`${userKey}_actions`, []);

    if(!updatedActions || updatedActions.length === 0) {
        console.warn("No actions to update.");
        return false;
    }

    try {
        const response = await fetchWithTimeout(`${ACTIONS_SERVER}update/${userKey}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedActions)
        });

        if (response.ok) {
            // Clear the cached actions after successful posting
            cacheUtils.remove(`${userKey}_actions`);
            return true;
        }
        else {
            console.warn("Failed to update actions. Response status: ", response.status);
            return false;
        }
    }
    catch (error) {
        console.error("Error during action data update: ", error);
        return false;
    }
}

// FIXME: Backend doesn't support 'days' parameter yet
// See READMES/backend/NOTIFICATIONS_TIME_RANGE_BACKEND_FIX.md for backend implementation needed
// For now, we fetch all notifications and filter them in the frontend
export async function fetchNotificationDataDB(daysBack = 30) {
    const userKey = getUserKey();
    if (!userKey) {
        console.warn("fetchNotificationDataDB: No user key found");
        return [];
    }

    let notificationData = [];
    try {
        // Currently, backend ignores the 'days' parameter and returns all notifications
        // We're including it in the URL for when backend support is added
        const url = `${NOTIFICATIONS_SERVER}${userKey}?days=${daysBack}`;
        console.log(`Fetching notifications (will filter to ${daysBack} days client-side):`, url);
        
        const response = await fetchWithTimeout(url, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error(`fetchNotificationDataDB: failed with status: ${response.status}`);
        }

        const data = await response.json();

        // Map the response to match our notification schema
        notificationData = data.map(notification => ({
            timestamp_utc: notification.timestamp_utc,
            sensor_description: notification.sensor_description,
            actuator_description: notification.actuator_description,
            actuator_type: notification.actuator_type,
            status: notification.status,
            proportion: notification.proportion,
            severity: notification.severity,
            message: notification.message,
            note_id: notification.note_id,
            user_id: notification.user_id,
            mac_address: notification.mac_address,
            username: notification.username
        }));

        // FRONTEND FILTERING: Filter notifications by date range since backend doesn't support it yet
        if (daysBack && notificationData.length > 0) {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - daysBack);
            
            notificationData = notificationData.filter(notification => {
                const notificationDate = parseNotificationTimestamp(notification.timestamp_utc);
                if (!notificationDate) {
                    console.warn('Invalid timestamp for notification:', notification);
                    return true; // Include notifications with invalid timestamps
                }
                return notificationDate >= cutoffDate;
            });
            
            console.log(`Filtered ${notificationData.length} notifications within last ${daysBack} days`);
        }

        if (notificationData.length === 0) {
            console.warn(`No notification data found for user: ${userKey} within ${daysBack} days`);
        }
    } catch (error) {
        console.error(`Error fetching notification data for user ${userKey}:`, error);
    }

    return notificationData;
}

//Universal response formatter:
export function formatApiResponse(response) {
    // If response already in the correct format, return it as is.
    if(response && response.data !== undefined) {
        return response;
    }
    // Handle different possible response structures.
    const data = response?.data || response;
    //Wrap the data in a standard data format:
    return {
        data: data,
        fromCache: false
    }
}

export async function loadCardDataDBCache() {   
    const userKey = getUserKey();
    if (!userKey) return { data: [], fromCache: false };

    // First, try to get cards from localStorage
    const cachedCards = cacheUtils.get(userKey, []);
    if (cachedCards && cachedCards.length > 0) {
        return { data: cachedCards, fromCache: true };
    }

    // If no cached cards, fetch from API
    try {
        const cardsData = await fetchCardDataDB(); 
        if (cardsData && cardsData.length > 0) {
            // Store in localStorage for backup
            cacheUtils.set(userKey, cardsData);
            return { data: cardsData, fromCache: false };
        }
        else {   
            return { data: [], fromCache: false };
        }
    }
    catch (error) {
        console.error("Failed to load card data:", error);
        return { data: [], fromCache: false };
    }
}


/**
 * Simple API throttling/debouncing utility
 * 
 * This prevents excessive API calls by tracking the last time
 * each endpoint was called and enforcing a minimum interval.
 */

// Track the last time each endpoint was called
const lastCallTimes = {};

// Default minimum time between calls for the same endpoint
const DEFAULT_MIN_INTERVAL = 5000; // 5 seconds

// Function to clear API cache and throttling for specific endpoints
export const clearApiCache = (urlPattern) => {
    if (!urlPattern) {
        // Clear all cache and throttling
        apiResponseCache.clear();
        Object.keys(lastCallTimes).forEach(key => delete lastCallTimes[key]);
        console.log('🧹 Cleared entire API cache and throttling');
        return;
    }
    
    // Clear cache entries that match the pattern
    const keysToDelete = [];
    for (const key of apiResponseCache.keys()) {
        if (key.includes(urlPattern)) {
            keysToDelete.push(key);
        }
    }
    
    keysToDelete.forEach(key => {
        apiResponseCache.delete(key);
        console.log('🧹 Cleared API cache for:', key);
    });
    
    // Throttle buckets are path prefixes like /api/actions, not the full URL.
    const patternEndpoint = throttleEndpoint(urlPattern);
    Object.keys(lastCallTimes).forEach(endpoint => {
        if (!endpoint) return;
        if (
            endpoint.includes(urlPattern) ||
            (endpoint.length > 1 && urlPattern.includes(endpoint)) ||
            (patternEndpoint && endpoint === patternEndpoint)
        ) {
            delete lastCallTimes[endpoint];
            console.log('🧹 Cleared throttling for:', endpoint);
        }
    });
};

const throttleEndpoint = (url) => {
    try {
        const urlObj = String(url).includes('http') ? new URL(url) : { pathname: String(url) };
        return urlObj.pathname.split('/').slice(0, 3).join('/');
    } catch {
        return '';
    }
};

/**
 * Check if an API call should be throttled based on its URL
 * @param {string} url - The URL to check
 * @param {number} minInterval - Minimum interval in ms between calls (default: 5000ms)
 * @returns {boolean} - True if the call should be allowed, false if it should be throttled
 */
export const shouldAllowApiCall = (url, minInterval = DEFAULT_MIN_INTERVAL) => {
  // Extract the base endpoint from the URL for tracking
  // This handles both full URLs and relative paths
  const endpoint = throttleEndpoint(url);
  
  const now = Date.now();
  const lastCall = lastCallTimes[endpoint] || 0;
  const timeSinceLastCall = now - lastCall;
  
  // If not enough time has passed since the last call to this endpoint
  if (timeSinceLastCall < minInterval) {
    // Commented out to reduce console noise
    // console.log(`🛑 API call throttled: ${endpoint} (${timeSinceLastCall}ms since last call)`);
    return false;
  }
  
  // Update the last call time
  lastCallTimes[endpoint] = now;
  return true;
};

/**
 * Enhanced fetch with throttling to prevent excessive API calls
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const throttledFetch = async (url, options = {}) => {
  const shouldAllow = shouldAllowApiCall(url);
  
  // If we should throttle this call, return cached data if available
  if (!shouldAllow) {
    // In a real implementation, you might return cached data here
    // For now, we'll just proceed with the fetch, but log the throttling
  }
  
  // Proceed with the fetch
  return fetch(url, options);
};

// ===== AUTOMATION API FUNCTIONS =====

// Care Plan API endpoints
const CARE_PLAN_SERVER = process.env.REACT_APP_CARE_PLAN_SERVER;
const ACTION_PLAN_SERVER = process.env.REACT_APP_ACTION_PLAN_SERVER;
const ACTION_TRIGGER_SERVER = process.env.REACT_APP_ACTION_TRIGGER_SERVER;
const CARE_PLAN_EVENT_SERVER = process.env.REACT_APP_CARE_PLAN_EVENT_SERVER;
const CARE_PLAN_SCHEDULE_SERVER = process.env.REACT_APP_CARE_PLAN_SCHEDULE_SERVER;
// Watering events (R3). Optional: until the Pi backend ships this endpoint, the env var is
// unset and all watering-event reads are disabled (see WATERING_EVENTS_BACKEND_SPEC_README.md).
const WATERING_EVENTS_SERVER = process.env.REACT_APP_WATERING_EVENTS_SERVER;

// True only when the backend endpoint is configured. Callers gate React Query `enabled` on this
// so the UI shows graceful placeholders (and makes zero network calls) before the backend exists.
export const isWateringEventsEnabled = () => !!WATERING_EVENTS_SERVER;

/**
 * Fetch all care plans for a user
 * @param {string} username - The username
 * @param {Object} filters - Optional filters (plan_type, is_active, etc.)
 * @returns {Promise<Array>} - Array of care plans
 */
export async function fetchCarePlans(username, filters = {}) {
    if (!username) {
        console.warn("fetchCarePlans: No username provided");
        return [];
    }

    try {
        // Filter out undefined values to prevent "undefined" strings in URL
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null)
        );
        const queryParams = new URLSearchParams(cleanFilters).toString();
        const url = `${CARE_PLAN_SERVER}${username}${queryParams ? `?${queryParams}` : ''}`;
        
        const response = await fetchWithTimeout(url, { method: 'GET' });
        
        if (!response.ok) {
            throw new Error(`fetchCarePlans failed with status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('fetchCarePlans - server response:', data);
        return data.care_plans || data.plans || data || [];
    } catch (error) {
        console.error(`Error fetching care plans for user ${username}:`, error);
        return [];
    }
}

/**
 * Create a new care plan
 * @param {string} username - The username
 * @param {Object} carePlanData - The care plan data
 * @returns {Promise<Object>} - The created care plan
 */
export async function createCarePlan(username, carePlanData) {
    if (!username) {
        throw new Error("createCarePlan: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${CARE_PLAN_SERVER}${username}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(carePlanData)
        });

        if (!response.ok) {
            throw new Error(`createCarePlan failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error creating care plan for user ${username}:`, error);
        throw error;
    }
}

/**
 * Update an existing care plan
 * @param {string} username - The username
 * @param {number} carePlanId - The care plan ID
 * @param {Object} updateData - The update data
 * @returns {Promise<Object>} - The updated care plan
 */
export async function updateCarePlan(username, carePlanId, updateData) {
    if (!username) {
        throw new Error("updateCarePlan: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${CARE_PLAN_SERVER}${username}/${carePlanId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData)
        });

        if (!response.ok) {
            throw new Error(`updateCarePlan failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error updating care plan ${carePlanId} for user ${username}:`, error);
        throw error;
    }
}

/**
 * Delete a care plan
 * @param {string} username - The username
 * @param {number} carePlanId - The care plan ID
 * @returns {Promise<boolean>} - Success status
 */
export async function deleteCarePlan(username, carePlanId) {
    if (!username) {
        throw new Error("deleteCarePlan: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${CARE_PLAN_SERVER}${username}/${carePlanId}`, {
            method: 'DELETE'
        });

        if (!response.ok) {
            throw new Error(`deleteCarePlan failed with status: ${response.status}`);
        }

        return true;
    } catch (error) {
        console.error(`Error deleting care plan ${carePlanId} for user ${username}:`, error);
        throw error;
    }
}

/**
 * Fetch action plans for a user
 * @param {string} username - The username
 * @param {Object} filters - Optional filters (care_plan_id, is_active, etc.)
 * @returns {Promise<Array>} - Array of action plans
 */
export async function fetchActionPlans(username, filters = {}) {
    if (!username) {
        console.warn("fetchActionPlans: No username provided");
        return [];
    }

    try {
        // Filter out undefined values to prevent "undefined" strings in URL
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null)
        );
        const queryParams = new URLSearchParams(cleanFilters).toString();
        const url = `${ACTION_PLAN_SERVER}${username}${queryParams ? `?${queryParams}` : ''}`;
        
        const response = await fetchWithTimeout(url, { method: 'GET' });
        
        if (!response.ok) {
            throw new Error(`fetchActionPlans failed with status: ${response.status}`);
        }
        
        const data = await response.json();
        return data.action_plans || data || [];
    } catch (error) {
        console.error(`Error fetching action plans for user ${username}:`, error);
        return [];
    }
}

/**
 * Create a new action plan
 * @param {string} username - The username
 * @param {Object} actionPlanData - The action plan data
 * @returns {Promise<Object>} - The created action plan
 */
export async function createActionPlan(username, actionPlanData) {
    if (!username) {
        throw new Error("createActionPlan: No username provided");
    }

    // Client-side validation for past dates
    if (actionPlanData.start_date) {
        const startDate = new Date(actionPlanData.start_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of today
        
        // Compare only the date part, not the time
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        
        // Check if it's a same-day plan
        const isSameDay = startDateOnly.toDateString() === today.toDateString();
        
        if (startDateOnly < today && !isSameDay) {
            throw new Error("Action plan start date cannot be in the past (except for same-day plans)");
        }
    }

    try {
        const response = await fetchWithTimeout(`${ACTION_PLAN_SERVER}${username}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(actionPlanData)
        });

        if (!response.ok) {
            throw new Error(`createActionPlan failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error creating action plan for user ${username}:`, error);
        throw error;
    }
}

/**
 * Update an existing action plan
 * @param {string} username - The username
 * @param {number} actionPlanId - The action plan ID
 * @param {Object} updateData - The update data
 * @returns {Promise<Object>} - The updated action plan
 */
export async function updateActionPlan(username, actionPlanId, updateData) {
    if (!username) {
        throw new Error("updateActionPlan: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${ACTION_PLAN_SERVER}${username}/${actionPlanId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData)
        });

        if (!response.ok) {
            throw new Error(`updateActionPlan failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error updating action plan ${actionPlanId} for user ${username}:`, error);
        throw error;
    }
}

/**
 * Delete an action plan
 * @param {string} username - The username
 * @param {number} actionPlanId - The action plan ID
 * @returns {Promise<boolean>} - Success status
 */
export async function deleteActionPlan(username, actionPlanId) {
    if (!username) {
        throw new Error("deleteActionPlan: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${ACTION_PLAN_SERVER}${username}/${actionPlanId}`, {
            method: 'DELETE'
        });

        if (!response.ok) {
            throw new Error(`deleteActionPlan failed with status: ${response.status}`);
        }

        return true;
    } catch (error) {
        console.error(`Error deleting action plan ${actionPlanId} for user ${username}:`, error);
        throw error;
    }
}

/**
 * Fetch action triggers for a user
 * @param {string} username - The username
 * @param {Object} filters - Optional filters (action_plan_id, is_enabled, etc.)
 * @returns {Promise<Array>} - Array of action triggers
 */
export async function fetchActionTriggers(username, filters = {}) {
    if (!username) {
        console.warn("fetchActionTriggers: No username provided");
        return [];
    }

    try {
        // Filter out undefined values to prevent "undefined" strings in URL
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null)
        );
        const queryParams = new URLSearchParams(cleanFilters).toString();
        const url = `${ACTION_TRIGGER_SERVER}${username}${queryParams ? `?${queryParams}` : ''}`;
        
        const response = await fetchWithTimeout(url, { method: 'GET' });
        
        if (!response.ok) {
            throw new Error(`fetchActionTriggers failed with status: ${response.status}`);
        }
        
        const data = await response.json();
        return data.action_triggers || data || [];
    } catch (error) {
        console.error(`Error fetching action triggers for user ${username}:`, error);
        return [];
    }
}

/**
 * Create a new action trigger
 * @param {string} username - The username
 * @param {Object} triggerData - The trigger data
 * @returns {Promise<Object>} - The created trigger
 */
export async function createActionTrigger(username, triggerData) {
    if (!username) {
        throw new Error("createActionTrigger: No username provided");
    }

    // Client-side validation for past dates
    if (triggerData.execution_date) {
        // Parse the date string more reliably by splitting it
        const [year, month, day] = triggerData.execution_date.split('-').map(Number);
        const executionDateOnly = new Date(year, month - 1, day); // month is 0-indexed
        
        const today = new Date();
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        console.log('Date validation debug:', {
            originalExecutionDate: triggerData.execution_date,
            parsedYear: year,
            parsedMonth: month,
            parsedDay: day,
            executionDateOnly: executionDateOnly.toISOString(),
            todayOnly: todayOnly.toISOString(),
            isPast: executionDateOnly < todayOnly
        });
        
        // Allow same-day triggers (today) and future dates
        if (executionDateOnly < todayOnly) {
            throw new Error("Action trigger execution date cannot be in the past (except for same-day triggers)");
        }
    }

    try {
        const response = await fetchWithTimeout(`${ACTION_TRIGGER_SERVER}${username}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(triggerData)
        });

        if (!response.ok) {
            throw new Error(`createActionTrigger failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error creating action trigger for user ${username}:`, error);
        throw error;
    }
}

/**
 * Update an existing action trigger
 * @param {string} username - The username
 * @param {number} triggerId - The trigger ID
 * @param {Object} updateData - The update data
 * @returns {Promise<Object>} - The updated trigger
 */
export async function updateActionTrigger(username, triggerId, updateData) {
    if (!username) {
        throw new Error("updateActionTrigger: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${ACTION_TRIGGER_SERVER}${username}/${triggerId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updateData)
        });

        if (!response.ok) {
            throw new Error(`updateActionTrigger failed with status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error updating action trigger ${triggerId} for user ${username}:`, error);
        throw error;
    }
}

/**
 * Delete an action trigger
 * @param {string} username - The username
 * @param {number} triggerId - The trigger ID
 * @returns {Promise<boolean>} - Success status
 */
export async function deleteActionTrigger(username, triggerId) {
    if (!username) {
        throw new Error("deleteActionTrigger: No username provided");
    }

    try {
        const response = await fetchWithTimeout(`${ACTION_TRIGGER_SERVER}${username}/${triggerId}`, {
            method: 'DELETE'
        });

        if (!response.ok) {
            throw new Error(`deleteActionTrigger failed with status: ${response.status}`);
        }

        return true;
    } catch (error) {
        console.error(`Error deleting action trigger ${triggerId} for user ${username}:`, error);
        throw error;
    }
}

/**
 * Fetch care plan events for a user
 * @param {string} username - The username
 * @param {Object} filters - Optional filters (care_plan_id, execution_status, etc.)
 * @returns {Promise<Array>} - Array of care plan events
 */
export async function fetchCarePlanEvents(username, filters = {}) {
    if (!username) {
        console.warn("fetchCarePlanEvents: No username provided");
        return [];
    }

    try {
        // Filter out undefined values to prevent "undefined" strings in URL
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null)
        );
        const queryParams = new URLSearchParams(cleanFilters).toString();
        const url = `${CARE_PLAN_EVENT_SERVER}${username}${queryParams ? `?${queryParams}` : ''}`;
        

        
        const response = await fetchWithTimeout(url, { method: 'GET' });
        

        
        if (!response.ok) {
            throw new Error(`fetchCarePlanEvents failed with status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();

        
        const result = data.events || data.care_plan_events || data || [];

        
        return result;
    } catch (error) {
        console.error(`[fetchCarePlanEvents] Error fetching care plan events for user ${username}:`, error);
        return [];
    }
}

/**
 * Fetch care plan execution schedule for a user
 * @param {string} username - The username
 * @param {Object} filters - Optional filters (action_plan_id, execution_status, etc.)
 * @returns {Promise<Array>} - Array of scheduled executions
 */
export async function fetchCarePlanSchedule(username, filters = {}) {
    if (!username) {
        console.warn("fetchCarePlanSchedule: No username provided");
        return [];
    }

    try {
        // Filter out undefined values to prevent "undefined" strings in URL
        const cleanFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== undefined && value !== null)
        );
        const queryParams = new URLSearchParams(cleanFilters).toString();
        const url = `${CARE_PLAN_SCHEDULE_SERVER}${username}${queryParams ? `?${queryParams}` : ''}`;
        
        const response = await fetchWithTimeout(url, { method: 'GET' });
        
        if (!response.ok) {
            throw new Error(`fetchCarePlanSchedule failed with status: ${response.status}`);
        }
        
        const data = await response.json();
        return data.schedule || data.care_plan_schedule || data || [];
    } catch (error) {
        console.error(`Error fetching care plan schedule for user ${username}:`, error);
        return [];
    }
}

// ============================================================================
// WATERING EVENTS (R3) — see READMES/irrigation/WATERING_EVENTS_BACKEND_SPEC_README.md
// ============================================================================
// These readers are intentionally "graceful": if the backend endpoint is not configured
// (env var unset) or returns 404/error, they resolve to a safe empty value instead of
// throwing. This lets the frontend ship before the Pi backend implements the endpoint.

// Normalize the summary payload (snake_case from Flask) into the camelCase shape the UI uses.
function mapWateringSummary(raw) {
    if (!raw || typeof raw !== 'object') return null;
    return {
        actionMacAddress: raw.action_mac_address ?? raw.actionMacAddress ?? null,
        wateringState: raw.watering_state ?? raw.wateringState ?? 'idle',
        lastWateredAt: raw.last_watered_at ?? raw.lastWateredAt ?? null,
        lastStatus: raw.last_status ?? raw.lastStatus ?? null,
        lastDurationSeconds: raw.last_duration_seconds ?? raw.lastDurationSeconds ?? null,
    };
}

function mapWateringEvent(raw) {
    if (!raw || typeof raw !== 'object') return null;
    return {
        eventId: raw.event_id ?? raw.eventId ?? null,
        actionMacAddress: raw.action_mac_address ?? raw.actionMacAddress ?? null,
        sensorMacAddress: raw.sensor_mac_address ?? raw.sensorMacAddress ?? null,
        actuatorType: raw.actuator_type ?? raw.actuatorType ?? null,
        startedAt: raw.started_at ?? raw.startedAt ?? null,
        endedAt: raw.ended_at ?? raw.endedAt ?? null,
        durationSeconds: raw.duration_seconds ?? raw.durationSeconds ?? null,
        status: raw.status ?? null,
        source: raw.source ?? null,
    };
}

/**
 * Current watering state + last completed run for one action (pump).
 * @param {string} actionMac
 * @returns {Promise<Object|null>} camelCase summary, or null when unavailable.
 */
export async function fetchWateringSummary(actionMac) {
    if (!WATERING_EVENTS_SERVER || !actionMac) return null;

    try {
        const params = new URLSearchParams({ action_mac: actionMac }).toString();
        const url = `${WATERING_EVENTS_SERVER}summary?${params}`;
        const response = await fetchWithTimeout(url, { method: 'GET' });

        // 404 / not-yet-implemented is expected before backend ships: treat as "no data".
        if (!response.ok) return null;

        const data = await response.json();
        return mapWateringSummary(data?.summary ?? data);
    } catch (error) {
        console.warn(`fetchWateringSummary: no data for ${actionMac} (${error?.message || error})`);
        return null;
    }
}

/**
 * List watering events, newest first, filtered by action and/or sensor MAC.
 * @param {{actionMac?: string, sensorMac?: string, limit?: number}} filters
 * @returns {Promise<Array>} camelCase events, or [] when unavailable.
 */
export async function fetchWateringEvents({ actionMac, sensorMac, limit = 50 } = {}) {
    if (!WATERING_EVENTS_SERVER || (!actionMac && !sensorMac)) return [];

    try {
        const clean = Object.fromEntries(
            Object.entries({ action_mac: actionMac, sensor_mac: sensorMac, limit })
                .filter(([, v]) => v !== undefined && v !== null)
        );
        const url = `${WATERING_EVENTS_SERVER}?${new URLSearchParams(clean).toString()}`;
        const response = await fetchWithTimeout(url, { method: 'GET' });

        if (!response.ok) return [];

        const data = await response.json();
        const list = data?.events ?? data?.watering_events ?? data ?? [];
        return Array.isArray(list) ? list.map(mapWateringEvent).filter(Boolean) : [];
    } catch (error) {
        console.warn(`fetchWateringEvents: no data (${error?.message || error})`);
        return [];
    }
}